import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject } from '../api/projects'
import { getRooms, createRoom, updateRoom, deleteRoom, Room } from '../api/rooms'
import { useState, useEffect } from 'react'

export default function RoomsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null)
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null)

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ['rooms', projectId],
    queryFn: () => getRooms(Number(projectId!)),
    enabled: !!projectId,
  })

  const createRoomMutation = useMutation({
    mutationFn: (roomData: Omit<Room, 'id'>) => createRoom(roomData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms', projectId] })
      setShowCreateForm(false)
      resetForm()
    },
    onError: (error: Error) => {
      console.error('Failed to create room:', error)
      alert(`Ошибка создания помещения: ${error.message}`)
    },
  })

  const updateRoomMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Room, 'id'>> }) => 
      updateRoom(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms', projectId] })
      setShowEditForm(false)
      setSelectedRoom(null)
      resetForm()
    },
    onError: (error: Error) => {
      console.error('Failed to update room:', error)
      alert(`Ошибка обновления помещения: ${error.message}`)
    },
  })

  const deleteRoomMutation = useMutation({
    mutationFn: (id: number) => deleteRoom(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms', projectId] })
    },
    onError: (error: Error) => {
      console.error('Failed to delete room:', error)
      alert(`Ошибка удаления помещения: ${error.message}`)
    },
  })

  const resetForm = () => {
    setSelectedRoom(null)
  }

  const handleCreateRoom = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const num = formData.get('num') as string
    const name = formData.get('name') as string

    if (!num.trim() || !name.trim()) {
      alert('Пожалуйста, заполните все поля')
      return
    }

    createRoomMutation.mutate({
      project_id: Number(projectId!),
      num: num.trim(),
      name: name.trim(),
    })
  }

  const handleEditRoom = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedRoom) return

    const formData = new FormData(e.currentTarget)
    const num = formData.get('num') as string
    const name = formData.get('name') as string

    if (!num.trim() || !name.trim()) {
      alert('Пожалуйста, заполните все поля')
      return
    }

    updateRoomMutation.mutate({
      id: selectedRoom.id,
      data: {
        num: num.trim(),
        name: name.trim(),
      },
    })
  }

  const handleEditClick = (room: Room) => {
    setSelectedRoom(room)
    setShowEditForm(true)
  }

  const handleDeleteClick = (room: Room) => {
    if (confirm(`Удалить помещение "${room.name}" (${room.num})?`)) {
      deleteRoomMutation.mutate(room.id)
    }
  }

  // Close modals on ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowCreateForm(false)
        setShowEditForm(false)
        setSelectedRoom(null)
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [])

  // Handle modal backdrop click
  const handleModalBackdrop = (e: React.MouseEvent, onClose: () => void) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (projectLoading || roomsLoading) {
    return <div className="text-center py-8">Загрузка...</div>
  }

  if (!project) {
    return <div className="text-center py-8 text-red-600">Проект не найден</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Назад к {project.name}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">🏠 Помещения</h1>
          <p className="text-gray-600 mt-1">
            Управление помещениями проекта
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          ➕ Создать помещение
        </button>
      </div>

      {/* Rooms Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {rooms.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg mb-2">Нет помещений</p>
            <p className="text-sm">Создайте первое помещение для проекта</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Номер
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Название
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rooms.map((room) => (
                  <tr key={room.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {room.num}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {room.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => handleEditClick(room)}
                          className="action-icon-btn edit"
                          title="Редактировать"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteClick(room)}
                          className="action-icon-btn delete"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Room Modal */}
      {showCreateForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => handleModalBackdrop(e, () => setShowCreateForm(false))}
          onMouseDown={(e) => setMouseDownTarget(e.target)}
          onMouseUp={(e) => {
            if (e.target === mouseDownTarget && e.target === e.currentTarget) {
              setShowCreateForm(false)
            }
            setMouseDownTarget(null)
          }}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Создать помещение</h2>
            <form onSubmit={handleCreateRoom} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Номер помещения <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="num"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="11.1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Спальня"
                  required
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createRoomMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createRoomMutation.isPending ? 'Создание...' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Room Modal */}
      {showEditForm && selectedRoom && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => handleModalBackdrop(e, () => {
            setShowEditForm(false)
            setSelectedRoom(null)
          })}
          onMouseDown={(e) => setMouseDownTarget(e.target)}
          onMouseUp={(e) => {
            if (e.target === mouseDownTarget && e.target === e.currentTarget) {
              setShowEditForm(false)
              setSelectedRoom(null)
            }
            setMouseDownTarget(null)
          }}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Редактировать помещение</h2>
            <form onSubmit={handleEditRoom} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Номер помещения <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="num"
                  defaultValue={selectedRoom.num}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  defaultValue={selectedRoom.name}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false)
                    setSelectedRoom(null)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={updateRoomMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateRoomMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

